import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Flex,
  SimpleGrid,
  Button,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

export default function AboutPage() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={8} align="stretch">
        {/* Hero Section */}
        <Box textAlign="center" mb={8}>
          <Heading as="h1" size="2xl" mb={4} color="brand.500">
            About MCPPM
          </Heading>
          <Text fontSize="xl" maxW="container.md" mx="auto">
            The Model Context Protocol Package Manager: Simplifying the deployment 
            and management of MCP servers.
          </Text>
        </Box>
        
        <Divider />
        
        {/* About Project */}
        <Box bg={bgColor} p={8} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading as="h2" size="lg" mb={4}>
            Our Mission
          </Heading>
          <Text mb={4}>
            MCPPM was created to simplify the way developers discover, install, and manage 
            Model Context Protocol (MCP) servers. Our goal is to build a robust ecosystem 
            of MCP servers that extend the capabilities of large language models.
          </Text>
          <Text mb={4}>
            With MCPPM, you can easily find, install, configure, and manage MCP servers 
            through a simple interface, whether you're using the CLI tool or our web registry.
          </Text>
        </Box>
        
        {/* Supported By Section */}
        <Box bg={bgColor} p={8} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading as="h2" size="lg" mb={6}>
            Supported By
          </Heading>
          
          <Flex direction={{ base: 'column', md: 'row' }} gap={8} align="center">
            <Box flex={1}>
              <Text fontSize="lg" fontWeight="bold" mb={2}>Polyhedra</Text>
              <Text mb={4}>
                MCPPM is proudly supported by Polyhedra, an organization committed to advancing 
                open-source AI infrastructure. Their support enables us to build tools that 
                facilitate the development and sharing of powerful AI capabilities.
              </Text>
              <Link href="https://polyhedra.ai" isExternal color="brand.500">
                <Button rightIcon={<ExternalLinkIcon />} size="sm" variant="outline">
                  Visit Polyhedra
                </Button>
              </Link>
            </Box>
            <Box flex={1} textAlign="center">
              {/* Placeholder for Polyhedra logo - update with actual logo path */}
              <Box 
                borderWidth="1px" 
                borderRadius="md" 
                p={4} 
                width="200px" 
                height="200px" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                mx="auto"
              >
                <Text fontWeight="bold" fontSize="lg">Polyhedra</Text>
              </Box>
            </Box>
          </Flex>
        </Box>
        
        {/* How It Works */}
        <Box bg={bgColor} p={8} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading as="h2" size="lg" mb={6}>
            How MCPPM Works
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box p={4} borderWidth="1px" borderRadius="md" textAlign="center">
              <Heading as="h3" size="md" mb={3}>Discover</Heading>
              <Text>
                Browse our registry of MCP servers or search for specific capabilities 
                to enhance your AI applications.
              </Text>
            </Box>
            
            <Box p={4} borderWidth="1px" borderRadius="md" textAlign="center">
              <Heading as="h3" size="md" mb={3}>Install</Heading>
              <Text>
                Use our CLI tool to install MCP servers with a single command, 
                directly from GitHub repositories.
              </Text>
            </Box>
            
            <Box p={4} borderWidth="1px" borderRadius="md" textAlign="center">
              <Heading as="h3" size="md" mb={3}>Manage</Heading>
              <Text>
                Start, stop, and configure your MCP servers with simple commands 
                or through our web interface.
              </Text>
            </Box>
          </SimpleGrid>
        </Box>
        
        {/* Get Involved */}
        <Box bg={bgColor} p={8} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <Heading as="h2" size="lg" mb={4}>
            Get Involved
          </Heading>
          <Text mb={4}>
            MCPPM is an open-source project, and we welcome contributions from the community. 
            Whether you're interested in developing new features, improving documentation, 
            or creating MCP servers, there are many ways to get involved.
          </Text>
          <Link 
            href="https://github.com/example/mcppm" 
            isExternal 
            color="brand.500"
          >
            <Button rightIcon={<ExternalLinkIcon />} colorScheme="brand">
              Contribute on GitHub
            </Button>
          </Link>
        </Box>
      </VStack>
    </Container>
  );
}
